/**
 * Site usage analytics client.
 *
 * Buffers events and flushes them in batches so we make one request per burst
 * of activity rather than one per click. Nothing here is allowed to break the
 * page it measures: every failure path swallows the error.
 *
 * Privacy: records route paths, element labels and timings only. It never
 * reads input values, form contents or keystrokes.
 *
 * Note on unload: navigator.sendBeacon cannot set an Authorization header, and
 * our API requires a bearer token, so we use fetch(..., {keepalive: true})
 * which can. Same "survives page teardown" guarantee, headers included.
 */

export interface AnalyticsEvent {
  eventType: 'pageview' | 'click' | 'custom';
  sessionId: string;
  path?: string;
  pageTitle?: string;
  target?: string;
  metadata?: Record<string, any>;
  durationMs?: number;
  referrer?: string;
  occurredAt: string;
  organizationId?: number | null;
}

type Sender = (payload: { events: AnalyticsEvent[]; userAgent: string }, useKeepalive: boolean) => Promise<void>;

const SESSION_KEY = 'ik_analytics_session';
const MAX_BATCH = 50;      // must not exceed the server's per-batch cap
const FLUSH_INTERVAL_MS = 15000;
const FLUSH_AT_COUNT = 20;

let queue: AnalyticsEvent[] = [];
let sender: Sender | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let currentOrgId: number | null = null;
let enabled = false;

/** Stable per-tab id. sessionStorage so a new tab is a new session. */
export const getSessionId = (): string => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled - fall back to a per-load id.
    return 'nostorage';
  }
};

/**
 * Wire up the transport. Called once the user is authenticated, since the
 * ingest endpoint requires a token. Until this runs, events are dropped rather
 * than queued indefinitely.
 */
export const configureAnalytics = (send: Sender, orgId: number | null) => {
  sender = send;
  currentOrgId = orgId;
  enabled = true;
  if (!timer) {
    timer = setInterval(() => { void flush(false); }, FLUSH_INTERVAL_MS);
  }
};

export const setAnalyticsOrg = (orgId: number | null) => { currentOrgId = orgId; };

/**
 * Stop the flush timer and disable capture.
 *
 * Deliberately does NOT clear the queue. This runs from a React effect cleanup,
 * which fires on any dependency change -- not just unmount. Dropping the buffer
 * here silently lost the landing pageview every time Auth0 handed back a new
 * getAccessTokenSilently identity during init.
 */
export const teardownAnalytics = () => {
  enabled = false;
  sender = null;
  if (timer) { clearInterval(timer); timer = null; }
};

export const flush = async (useKeepalive: boolean): Promise<void> => {
  if (!sender || queue.length === 0) return;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(batch.length);
  try {
    await sender({ events: batch, userAgent: navigator.userAgent }, useKeepalive);
  } catch {
    // Deliberately silent. Dropping analytics is always preferable to
    // surfacing an error, and re-queueing risks unbounded growth offline.
  }
};

const enqueue = (event: AnalyticsEvent) => {
  if (!enabled) return;
  queue.push(event);
  if (queue.length >= FLUSH_AT_COUNT) void flush(false);
};

const nowIso = () => new Date().toISOString();

export const trackPageview = (path: string, pageTitle?: string, durationMs?: number) => {
  enqueue({
    eventType: 'pageview',
    sessionId: getSessionId(),
    path,
    pageTitle: pageTitle || document.title,
    durationMs,
    referrer: document.referrer || undefined,
    occurredAt: nowIso(),
    organizationId: currentOrgId,
  });
};

/**
 * Record a click. `target` should be a human-readable label ("Export CSV"),
 * never user-entered text.
 */
export const trackClick = (target: string, metadata?: Record<string, any>) => {
  enqueue({
    eventType: 'click',
    sessionId: getSessionId(),
    path: window.location.pathname,
    target,
    metadata,
    occurredAt: nowIso(),
    organizationId: currentOrgId,
  });
};

export const trackEvent = (target: string, metadata?: Record<string, any>) => {
  enqueue({
    eventType: 'custom',
    sessionId: getSessionId(),
    path: window.location.pathname,
    target,
    metadata,
    occurredAt: nowIso(),
    organizationId: currentOrgId,
  });
};
